import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ExternalLink, RefreshCw, ShieldAlert, X } from "lucide-react";
import { incidentsApi } from "./api";
import type { IncidentCategory, IncidentSeverity, SecurityIncident } from "./types";
import { StatusBadge } from "../applications/shared/StatusBadge";
import { FiltersBar } from "../applications/shared/FiltersBar";
import {
  ApplicationsTable,
  type Column,
} from "../applications/shared/ApplicationsTable";
import { Section, Field, Link } from "../applications/shared/InfoBlock";
import { formatApplicantName, formatDate, formatDateTime } from "../applications/shared/format";
import { personDetail, personName } from "./shared/person";
import { LoadingBlock, ErrorBlock } from "../dashboard/shared/StateBlocks";
import {
  INCIDENT_CATEGORY_LABELS,
  INCIDENT_SEVERITY_LABELS,
  INCIDENT_STATUS_LABELS,
  incidentStatusTone,
  isGraveCategory,
} from "./shared/labels";
import { useDebounce } from "@/lib/utils/useDebounce";

const CATEGORY_OPTIONS = [
  { value: "violence", label: "Violencia" },
  { value: "weapons_drugs", label: "Armas / drogas" },
  { value: "theft", label: "Robo" },
  { value: "sexual_abuse", label: "Abuso sexual" },
  { value: "drunk_aggressive", label: "Ebriedad agresiva" },
  { value: "unpaid", label: "Impago" },
  { value: "misconduct", label: "Mala conducta" },
  { value: "other", label: "Otro" },
];

const PAGE_SIZE = 30;
const FETCH_LIMIT = 200;

export function IncidentsScreen() {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => setPage(1), [debouncedSearch, category]);

  const listQuery = useQuery({
    queryKey: ["governance", "incidents", { limit: FETCH_LIMIT }],
    queryFn: () => incidentsApi.list({ limit: FETCH_LIMIT, offset: 0 }),
  });

  const filtered = useMemo(() => {
    const all = listQuery.data ?? [];
    return all.filter((i) => {
      if (category && i.category !== category) return false;
      if (debouncedSearch) {
        const q = debouncedSearch.toLowerCase();
        const haystack = [
          i.description,
          i.location,
          i.identifiedUser?.email,
          i.identifiedUser?.firstName,
          i.identifiedUser?.lastName,
          i.subject?.displayName,
          i.subject?.rutTail,
          i.club?.name,
          i.club?.nombre,
          i.event?.nombre,
          i.reporterName,
          i.reporterEmail,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [listQuery.data, category, debouncedSearch]);

  const paginated = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const selected = useMemo(
    () => (selectedId !== null ? (listQuery.data ?? []).find((i) => i.id === selectedId) : null),
    [listQuery.data, selectedId],
  );

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Governance · Incidentes</p>
          <h1 className="nx-h1">Incidentes de seguridad</h1>
          <p className="text-sm text-fg-muted mt-1">
            Vista cross-club de incidentes reportados desde Staff. Read-only:
            la gestión (resolución, anulación) la hace cada productor desde el
            Hub. Útil para soporte y monitoreo de patrones.
          </p>
        </div>
        <button
          onClick={() => listQuery.refetch()}
          className="nx-btn-secondary"
          disabled={listQuery.isFetching}
        >
          <RefreshCw size={14} className={listQuery.isFetching ? "animate-spin" : ""} />
          Actualizar
        </button>
      </header>

      <FiltersBar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por descripción, club, usuario, denunciante…"
        status={category}
        onStatusChange={setCategory}
        statusOptions={CATEGORY_OPTIONS}
      />

      {listQuery.isLoading ? (
        <LoadingBlock label="Cargando incidentes…" />
      ) : listQuery.isError ? (
        <ErrorBlock onRetry={() => listQuery.refetch()} />
      ) : (
        <>
          {(listQuery.data?.length ?? 0) >= FETCH_LIMIT && (
            <div className="text-2xs text-fg-subtle text-right -mb-2">
              Mostrando los {FETCH_LIMIT} más recientes. Si necesitás más, ajustar el query.
            </div>
          )}
          <ApplicationsTable<SecurityIncident>
            rows={paginated}
            columns={columns}
            page={page}
            pageSize={PAGE_SIZE}
            total={filtered.length}
            onPageChange={setPage}
            onRowClick={(r) => setSelectedId(r.id)}
            selectedId={selectedId}
          />
        </>
      )}

      {selected && <IncidentDrawer data={selected} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

const columns: Column<SecurityIncident>[] = [
  {
    key: "id",
    header: "ID",
    cell: (r) => <span className="text-fg-subtle tabular-nums">#{r.id}</span>,
    width: "min",
  },
  {
    key: "severity",
    header: "Severidad",
    cell: (r) => <SeverityBadge severity={r.severity} category={r.category} />,
    width: "min",
  },
  {
    key: "category",
    header: "Categoría",
    cell: (r) => (
      <span className="text-fg text-xs">
        {r.category ? INCIDENT_CATEGORY_LABELS[r.category] : "—"}
      </span>
    ),
  },
  {
    key: "club",
    header: "Club / Evento",
    cell: (r) => (
      <div>
        <div className="font-medium text-fg">{r.club?.name || r.club?.nombre || "—"}</div>
        <div className="text-2xs text-fg-subtle line-clamp-1">{r.event?.nombre ?? "—"}</div>
      </div>
    ),
  },
  {
    key: "user",
    header: "Persona identificada",
    cell: (r) =>
      r.identifiedUser || r.subject ? (
        <div>
          <div className="text-xs text-fg">{personName(r.identifiedUser, r.subject)}</div>
          <div className="text-2xs text-fg-subtle break-all">{personDetail(r.identifiedUser, r.subject)}</div>
        </div>
      ) : (
        <span className="text-fg-subtle text-xs italic">No identificado</span>
      ),
  },
  {
    key: "status",
    header: "Estado",
    cell: (r) => (
      <StatusBadge
        status={r.status}
        tone={incidentStatusTone(r.status)}
        label={INCIDENT_STATUS_LABELS[r.status]}
      />
    ),
    width: "min",
  },
  {
    key: "timestamp",
    header: "Reportado",
    cell: (r) => <span className="text-fg-subtle text-xs">{formatDate(r.timestamp)}</span>,
    width: "min",
    align: "right",
  },
];

function SeverityBadge({
  severity,
  category,
}: {
  severity: IncidentSeverity;
  category: IncidentCategory | null;
}) {
  const grave = isGraveCategory(category);
  if (grave || severity === "high") {
    return (
      <span className="nx-badge-danger inline-flex items-center gap-1">
        <ShieldAlert size={10} /> {INCIDENT_SEVERITY_LABELS[severity]}
      </span>
    );
  }
  return <span className="nx-badge-warning">{INCIDENT_SEVERITY_LABELS.low}</span>;
}

function IncidentDrawer({
  data,
  onClose,
}: {
  data: SecurityIncident;
  onClose: () => void;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const grave = isGraveCategory(data.category);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40 animate-fade-in" onClick={onClose} aria-hidden />
      <aside
        className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[560px] bg-bg-card border-l border-border shadow-xl flex flex-col animate-slide-in-right"
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-start justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 className="nx-h2 truncate">
              Incidente #{data.id} ·{" "}
              {data.category ? INCIDENT_CATEGORY_LABELS[data.category] : "Sin categoría"}
            </h2>
            <p className="text-2xs text-fg-subtle mt-0.5">{formatDateTime(data.timestamp)}</p>
          </div>
          <button onClick={onClose} className="nx-btn-ghost p-1.5" aria-label="Cerrar">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="flex flex-col gap-5">
            <div className="flex items-center gap-2 flex-wrap">
              <SeverityBadge severity={data.severity} category={data.category} />
              <StatusBadge
                status={data.status}
                tone={incidentStatusTone(data.status)}
                label={INCIDENT_STATUS_LABELS[data.status]}
              />
              {grave && (
                <span className="text-2xs text-danger inline-flex items-center gap-1">
                  <AlertTriangle size={10} /> Categoría elegible para ban global
                </span>
              )}
            </div>

            <Section title="Contexto">
              <Field label="Club" value={data.club?.name || data.club?.nombre || "—"} />
              <Field label="Evento" value={data.event?.nombre ?? "—"} />
              <Field label="Ubicación" value={data.location ?? "—"} wide />
              <Field label="Reportado por staff" value={formatApplicantName(data.reportedBy)} wide />
            </Section>

            <Section title="Descripción">
              <Field
                label="Detalle"
                value={
                  data.description ? (
                    <p className="whitespace-pre-wrap text-fg">{data.description}</p>
                  ) : (
                    "—"
                  )
                }
                wide
              />
            </Section>

            <Section title="Persona">
              <Field
                label="Identificada"
                value={
                  data.identifiedUser || data.subject
                    ? `${personName(data.identifiedUser, data.subject)} (${personDetail(data.identifiedUser, data.subject)})`
                    : "No identificada"
                }
                wide
              />
              {data.imageUrl && (
                <Field
                  label="Foto (ghost ban)"
                  value={
                    <img
                      src={data.imageUrl}
                      alt="Evidencia"
                      className="max-w-full h-40 object-cover rounded border border-border"
                    />
                  }
                  wide
                />
              )}
            </Section>

            {(data.reporterName || data.policeReportNumber) && (
              <Section title="Denunciante (datos para postulación a ban global)">
                <Field label="Nombre" value={data.reporterName ?? "—"} />
                <Field label="Teléfono" value={data.reporterPhone ?? "—"} />
                <Field
                  label="Email"
                  value={
                    data.reporterEmail ? (
                      <a className="text-accent hover:underline" href={`mailto:${data.reporterEmail}`}>
                        {data.reporterEmail}
                      </a>
                    ) : (
                      "—"
                    )
                  }
                  wide
                />
                <Field label="N° denuncia policial" value={data.policeReportNumber ?? "—"} wide />
              </Section>
            )}

            {data.evidenceUrls && data.evidenceUrls.length > 0 && (
              <Section title={`Evidencia (${data.evidenceUrls.length})`}>
                <div className="col-span-2 flex flex-col gap-2">
                  {data.evidenceUrls.map((url, i) => (
                    <Link key={i} href={url}>
                      <span className="inline-flex items-center gap-1">
                        <ExternalLink size={12} /> Archivo {i + 1}
                      </span>
                    </Link>
                  ))}
                </div>
              </Section>
            )}

            {data.status === "revoked" && (
              <Section title="Anulación">
                <Field label="Anulado el" value={formatDateTime(data.revokedAt)} />
                <Field label="Por" value={data.revokedBy?.email ?? "—"} />
                <Field
                  label="Motivo"
                  value={
                    data.revokeReason ? (
                      <p className="whitespace-pre-wrap text-fg">{data.revokeReason}</p>
                    ) : (
                      "—"
                    )
                  }
                  wide
                />
              </Section>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}
